import { Chart, type ChartOptions } from 'chart.js';

const TOUCH_STICKY_TOOLTIP_PLUGIN_ID = 'touchStickyTooltip';
const stickyTooltipState = new WeakMap<any, { activeElements: Array<{ datasetIndex: number; index: number }>; position: { x: number; y: number } }>();
const stickyTooltipDismissListeners = new WeakMap<any, () => void>();

const isTouchOrCoarsePointerDevice = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const hasCoarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse), (any-pointer: coarse)').matches;
    const hasTouchPoints = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

    return hasCoarsePointer || hasTouchPoints;
};

const isStickyTooltipPluginEnabled = (chart: any) => {
    const pluginOptions = chart?.options?.plugins?.[TOUCH_STICKY_TOOLTIP_PLUGIN_ID];

    if (pluginOptions === false) {
        return false;
    }

    if (typeof pluginOptions === 'object' && pluginOptions !== null && pluginOptions.enabled === false) {
        return false;
    }

    return true;
};

const clearStickyTooltipDismissListener = (chart: any) => {
    const removeListener = stickyTooltipDismissListeners.get(chart);
    if (!removeListener) {
        return;
    }

    removeListener();
    stickyTooltipDismissListeners.delete(chart);
};

const hideStickyTooltip = (chart: any) => {
    chart?.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
    stickyTooltipState.delete(chart);
    chart?.update?.();
};

const keepTooltipSticky = (chart: any) => {
    const activeElements = chart?.tooltip?.getActiveElements?.() ?? [];
    if (!activeElements.length) {
        clearStickyTooltipDismissListener(chart);
        hideStickyTooltip(chart);
        return;
    }

    const normalizedActiveElements = activeElements.map((element: any) => ({
        datasetIndex: element.datasetIndex,
        index: element.index,
    }));

    const position = {
        x: chart?.tooltip?.caretX ?? 0,
        y: chart?.tooltip?.caretY ?? 0,
    };

    stickyTooltipState.set(chart, {
        activeElements: normalizedActiveElements,
        position,
    });

    chart.tooltip.setActiveElements(normalizedActiveElements, position);
    chart.update();

    clearStickyTooltipDismissListener(chart);

    const onNextTap = (event: Event) => {
        if (event.target instanceof Node && chart?.canvas?.contains(event.target)) {
            return;
        }

        clearStickyTooltipDismissListener(chart);
        hideStickyTooltip(chart);
    };

    document.addEventListener('pointerdown', onNextTap, true);
    stickyTooltipDismissListeners.set(chart, () => {
        document.removeEventListener('pointerdown', onNextTap, true);
    });
};

const touchStickyTooltipPlugin = {
    id: TOUCH_STICKY_TOOLTIP_PLUGIN_ID,
    defaults: {
        enabled: true,
    },
    afterEvent(chart: any, args: any) {
        if (!isTouchOrCoarsePointerDevice() || !isStickyTooltipPluginEnabled(chart)) {
            return;
        }

        const eventType = args?.event?.type;
        if (!eventType) {
            return;
        }

        if ((eventType === 'click' || eventType === 'touchstart') && args.inChartArea) {
            keepTooltipSticky(chart);
            return;
        }

        const state = stickyTooltipState.get(chart);
        if (!state) {
            return;
        }

        const activeElements = chart?.tooltip?.getActiveElements?.() ?? [];
        if (activeElements.length === 0) {
            chart.tooltip.setActiveElements(state.activeElements, state.position);
            chart.update();
        }
    },
    beforeDestroy(chart: any) {
        clearStickyTooltipDismissListener(chart);
        stickyTooltipState.delete(chart);
    },
    afterDestroy(chart: any) {
        clearStickyTooltipDismissListener(chart);
        stickyTooltipState.delete(chart);
    },
};

let touchStickyTooltipPluginRegistered = false;

const ensureTouchStickyTooltipPluginRegistered = () => {
    if (touchStickyTooltipPluginRegistered) {
        return;
    }

    Chart.register(touchStickyTooltipPlugin as any);
    touchStickyTooltipPluginRegistered = true;
};

ensureTouchStickyTooltipPluginRegistered();

/**
 * Platform color palette for charts.
 * Single source of truth — use these instead of hex literals.
 */
export const chartColors = {
    primary: '#2C54EA',
    primaryLight: '#5577F5',
    blue: '#3B82F6',
    green: '#7BD3A4',
    greenDark: '#10b981',
    red: '#EBB6B7',
    amber: '#F59E0B',
    grey: '#ADB5BD',
    body: '#181924',

    // Testing-compliance status palette. Mirrors the
    // --color-compliance-* tokens in resources/css/app.css, which
    // chart.js can't resolve (canvas fillStyle needs raw hex).
    complianceApproved: '#22C55E',
    compliancePending: '#6387FA',
    complianceUnscheduled: '#F59E0B',
    complianceUntested: '#E5E7EB',
    complianceInoperable: '#E55353',

    primaryFill: 'rgba(44, 84, 234, 0.3)',
    redFill: 'rgba(235, 182, 183, 0.7)',
    greenFill: 'rgba(123, 211, 164, 0.5)',
    amberFill: 'rgba(245, 158, 11, 0.1)',
    redFillLight: 'rgba(235, 182, 183, 0.1)',
    thresholdLine: 'rgba(235, 182, 183, 0.8)',
} as const;

/**
 * Light tooltip config — overrides CoreUI's dark default.
 */
export const lightTooltip = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    titleColor: '#333',
    bodyColor: '#666',
    borderColor: '#ddd',
    borderWidth: 1,
    usePointStyle: true,
    boxWidth: 8,
} as const;

export function withTouchStickyTooltip<T extends Partial<ChartOptions>>(options: T): T {
    ensureTouchStickyTooltipPluginRegistered();

    const existingPlugins = (options.plugins ?? {}) as Record<string, any>;
    const existingTouchOptions = existingPlugins[TOUCH_STICKY_TOOLTIP_PLUGIN_ID];

    return {
        ...options,
        plugins: {
            ...existingPlugins,
            [TOUCH_STICKY_TOOLTIP_PLUGIN_ID]: {
                ...(typeof existingTouchOptions === 'object' && existingTouchOptions !== null ? existingTouchOptions : {}),
                enabled: true,
            },
        },
    } as T;
}

/**
 * Base chart options for all chart widgets
 * Provides consistent responsive behavior, animation, legend, and layout settings
 */
export function getBaseChartOptions(): Partial<ChartOptions> {
    return withTouchStickyTooltip({
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 750,
        },
        plugins: {
            legend: {
                display: false,
            },
        },
        layout: {
            padding: {
                top: 20,
            },
        },
    });
}

/**
 * Mobile-aware X-axis configuration
 * Truncates labels on mobile viewports (< 575px)
 */
export function getBaseMobileXAxisConfig() {
    const isMobile = window.innerWidth < 575;

    return {
        ticks: {
            callback: function (value: any, index: number, ticks: any[]) {
                const label = this.getLabelForValue(value);
                if (isMobile && label.length > 10) {
                    return label.substring(0, 10) + '...';
                }
                return label;
            },
        },
    };
}
